/**
* @typedef ClientLog
* @property {string} MetricsFailure When our Metrics Driver fails to initialize
* @property {string} DOMManipulationException Error when manipulating the DOM
* @property {string} ComponentUnhandledException Uncaught error in our application which bubbles up to an error boundary
* @property {string} PayloadProcessingError When there's an error processing payload data
* @property {string} PayloadValidationError When payload data validation fails
* @property {string} IllegalInternalDataState When data that should exist is not found or in an unexpected state
* @property {string} APIRequestFailure When an API request fails with an error response
* @property {string} APICriticalError When an API request fails due to a non-retryable server error (e.g., status codes 500, 501)
* @property {string} APITransientError When an API request fails likely due to a transient issue (e.g., status codes 502, 503, 504). Typically retryable
* @property {string} APIBadRequestError When an API request fails due to a bad or malformed request (status code 400)
* @property {string} APIClientError When an API request fails due to a client-side fault (status code 4xx)
* @property {string} APIThrottlingError When an API request fails due to throttling (status code 429)
* @property {string} InputValidationError When user or system input fails validation
* @property {string} MetaTagProcessingException When an error occurs processing meta tags from the document header
* @property {string} TranslationNotFound When a translation string is not found in the bundle
* @property {string} LegacyIdAccess When a legacy/deprecated identifier is used and mapped to a current identifier
* @property {string} BeaconSubmissionFailure When navigator.sendBeacon fails to queue data for transfer
* @property {string} RefParameterReceived When a ref parameter is received in the URL
* @property {string} WidgetIntegrationView When a widget page is viewed/loaded
* @property {string} WidgetIntegrationClick When a widget card is clicked by the user
* @property {string} AuthenticationExpiredRedirect When a redirect to sign-in is triggered due to expired authentication detected via API 412
* @property {string} VideoPlaybackError When video playback fails in the ReactPlayer component
* @property {string} APIRequestLatency When logging API request latency metrics for CloudWatch metric filter extraction.
*   Uses 'Latency.' prefix for CloudWatch metric filter grouping to distinguish latency metrics from error logs.
*/
export enum ClientLog {
  MetricsFailure = 'MetricsFailure',
  DOMManipulationException = 'DOMManipulationException',
  ComponentUnhandledException = 'ComponentUnhandledException',
  PayloadProcessingError = 'PayloadProcessingError',
  PayloadValidationError = 'PayloadValidationError',
  IllegalInternalDataState = 'IllegalInternalDataState',
  APIRequestFailure = 'APIRequestFailure',
  APICriticalError = 'APICriticalError',
  APITransientError = 'APITransientError',
  APIBadRequestError = 'APIClientBadRequestError',
  APIClientError = 'APIClientError',
  APIThrottlingError = 'APIThrottlingError',
  InputValidationError = 'InputValidationError',
  MetaTagProcessingException = 'MetaTagProcessingException',
  TranslationNotFound = 'TranslationNotFound',
  LegacyIdAccess = 'LegacyIdAccess',
  BeaconSubmissionFailure = 'BeaconSubmissionFailure',
  RefParameterReceived = 'RefParameterReceived',
  WidgetIntegrationView = 'WidgetIntegrationView',
  WidgetIntegrationClick = 'WidgetIntegrationClick',
  AuthenticationExpiredRedirect = 'AuthenticationExpiredRedirect',
  VideoPlaybackError = 'VideoPlaybackError',
  /**
   * Latency metrics use a 'Latency.' prefix for CloudWatch metric filter grouping.
   * This allows metric filters to easily distinguish latency measurements from error logs
   * and enables consistent namespace organization in CloudWatch dashboards.
   */
  APIRequestLatency = 'Latency.APIRequestLatency',
}
